/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleType,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  dictionaryValueFormatter,
  EntityInputDialogComponent,
  EntityInputDialogConfig,
  rendererIconByName,
  ROW_BOLD_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  ActionFlow,
  createDialogRoute,
  DateTimeTypes,
  EnumDictionary,
  FormStateSnapshot,
  getRouteInitData,
  getRouteParam,
  getRouteStaticData,
  LocalizationBusinessService,
  setEntityField,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { ID, WithUniqueId } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  Confirmation,
  DialogService,
  FormMode,
  WaitingContext,
  WaitingMode,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { lowerCase, map } from 'lodash';

import { ScheduleTaskDetailsBusinessService } from '../../resources/schedule-task-details-business.service';
import {
  createTaskCurrentTaskListItem,
  DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE,
  getScheduleTaskNoteId,
  getScheduleTaskTodoId,
  isExistsCcResourceGeneralSubtab,
  isExistsUserResourceGeneralSubtab,
  ScheduleTaskCcResourceListItem,
  ScheduleTaskDetails,
  ScheduleTaskDetailsGeneralSubtabName,
  ScheduleTaskDetailsTabName,
  ScheduleTaskInfo,
  ScheduleTaskNoteListItem,
  ScheduleTaskRelatedResourceTaskListItem,
  ScheduleTaskTodoDialogConfig,
  ScheduleTaskTodoListItem,
  ScheduleTaskUserResourceListItem,
} from '../../resources/schedule-task-details.model';

import { ScheduleTaskDetailsFormState } from './schedule-task-details-form.service';
import { scheduleTaskTodoDialog } from './schedule-task-todo-dialog/schedule-task-todo-dialog.route';
import { Observable } from 'rxjs';
import { of$ } from '@gh/rx';
import { WaitingProcess } from '../../../core-ui/waiting/waiting-process';

export const scheduleTemplateNoteListItemDialog = createDialogRoute({
  component: EntityInputDialogComponent,
  init: ScheduleTaskDetailsBusinessService,
});

@Component({
  standalone: false,
  selector: 'gh-scheduling-schedule-template-task-details',
  templateUrl: 'schedule-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ScheduleTaskDetailsFormState,
  ],
})
export class ScheduleTaskDetailsComponent extends AbstractFormComponent<ScheduleTaskDetails>
  implements OnInit {

  scheduleTemplateTaskDetailsTabName = ScheduleTaskDetailsTabName;
  scheduleTemplateTaskDetailsGeneralSubtabName = ScheduleTaskDetailsGeneralSubtabName;
  saveBaseDatesAnswer?: Confirmation;
  scheduleType: ScheduleType;
  waitingProcess: WaitingProcess;
  scheduleTaskTodoEventType: EnumDictionary<ScheduleTaskTodoEventType>;
  changeDateAfterCompleteTypes: EnumDictionary<ScheduleTaskChangeDateAfterCompleteType>;
  scheduleTaskTypes: EnumDictionary<ScheduleTaskType>;
  systemRoles: EnumDictionary<UserSystemRole>;
  taskDetails: ScheduleTaskDetails;
  ccResourceGridDefinition: GridDefinition;
  userResourceGridDefinition: GridDefinition;
  currentTaskListGridDefinition: GridDefinition;
  notesGridDefinition: LayoutBasedGridDefinition;
  todoGridDefinition: LayoutBasedGridDefinition;
  taskId: ID;
  templateId: ID;
  scheduleId: ID;
  parentTitleLabels = {
    [ScheduleType.Construction]: 'lbl_construction_sch',
    [ScheduleType.Sales]: 'lbl_sales_sch',
    [ScheduleType.PreConstruction]: 'lbl_pre_construction_sch',
    [ScheduleType.Maintenance]: 'lbl_maintenance_sch',
  };

  pageTitleLabels = {
    [ScheduleType.Construction]: 'lbl_construction_schedule_task',
    [ScheduleType.Sales]: 'lbl_sales_schedule_task',
    [ScheduleType.PreConstruction]: 'lbl_pre_construction_schedule_task',
    [ScheduleType.Maintenance]: 'lbl_maintenance_schedule_task',
  };

  helpIds = {
    [ScheduleType.Construction]: 'schedules/constructions/tasks/details',
    [ScheduleType.Sales]: 'schedules/sales/tasks/details',
    [ScheduleType.PreConstruction]: 'schedules/pre-constructions/tasks/details',
    [ScheduleType.Maintenance]: 'schedules/warranty/tasks/details',
  };

  titlePrefixIds = {
    [ScheduleType.Construction]: 'lbl_construction_sch_for_job',
    [ScheduleType.Sales]: 'lbl_sales_sch_for_sf',
    [ScheduleType.PreConstruction]: 'lbl_pre_construction_sch_for_job',
    [ScheduleType.Maintenance]: 'lbl_maintenance_sch_for_job',
  };

  constructor(public form: ScheduleTaskDetailsFormState,
              private businessService: ScheduleTaskDetailsBusinessService,
              private userContext: BuilderUserContext,
              private gridDefinitionFactory: GridDefinitionFactory,
              private pipelines: FormActionPipelines,
              private waitingContext: WaitingContext,
              private commonActions: CommonActions,
              private translationService: TranslationService,
              private dialogService: DialogService,
              private localizationBusinessService: LocalizationBusinessService,
              private route: ActivatedRoute) {
    super();

    this.displayTask = this.displayTask.bind(this);
  }
  get parentUrl(): string | undefined {
    const route: string = this.route.snapshot.queryParams?.parentRoute;
    if (!route) {
      return undefined;
    }
    if (route.includes('/new/gantt-chart') && this.scheduleId) {
        return route.replace('/new/gantt-chart', `/${this.scheduleId}/gantt-chart`);
    }
    return undefined;
  }
  get isJobSchedule(): boolean {
    return this.scheduleType !== ScheduleType.Sales;
  }

  get helpId(): string {
    return this.helpIds[this.scheduleType];
  }

  get parentTitle(): string {
    return this.parentTitleLabels[this.scheduleType];
  }

  get pageTitle(): string {
    return this.pageTitleLabels[this.scheduleType];
  }

  get titlePrefix(): string {
    return this.titlePrefixIds[this.scheduleType];
  }

  get isExistsCcResourceGeneralSubtab(): boolean {
    return isExistsCcResourceGeneralSubtab(this.taskDetails.type, this.taskDetails.isIncludedTasksExist);
  }

  get isExistsUserResourceGeneralSubtab(): boolean {
    return isExistsUserResourceGeneralSubtab(this.taskDetails.type, this.taskDetails.isIncludedTasksExist);
  }

  get isExistAnyResourceSubtab(): boolean {
    return this.isExistsCcResourceGeneralSubtab || this.isExistsUserResourceGeneralSubtab;
  }

  get completeFlow(): ActionFlow<FormFlowState<ScheduleTaskDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.form.isTopFormInvalid(
          this.taskDetails.info.value.confirmedForDate,
          this.taskDetails.info.value.confirmedOnDate),
        () => this.handleErrors()),
      execute: this.commonActions.execute((state: FormFlowState<ScheduleTaskDetails>) =>
                                            this.schSaveBaseDatesDialog(state.value).pipe(
                                              switchMap$(
                                                () => this.businessService.update(
                                                  this.scheduleType,
                                                  this.scheduleId,
                                                  this.taskId,
                                                  state.value,
                                                  this.saveBaseDatesAnswer).pipe(map$((value) => {
                                                    this.waitingProcess.stop();
                                                    this.saveBaseDatesAnswer = undefined;
                                                    return value;
                                                })))),
                                          true,
                                          () => {
                                            this.waitingProcess.stop();
                                          },
       ),
      finish: ({ value }: FormFlowState<ScheduleTaskDetails>) => {
        this.taskDetails = value;
        this.waitingProcess.stop();
        this.form.build(value, this.isFormEditMode());
      },
    });
  }

  displayTask(task: ScheduleTaskRelatedResourceTaskListItem): string {
    if (this.scheduleType === ScheduleType.Sales) {
      return `${task.scheduleFor} - ${task.name}`;
    } else {
      return `${task.scheduleFor} - ${task.name}`;
    }
  }

  handleErrors(): void {
    this.form.highlightInfoSnapshot();
    this.form.selectTab(ScheduleTaskDetailsTabName.General);
  }

  hasPermission(permission: string): boolean {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return this.userContext.hasPermission((BuilderPermission.Schedules.Construction as any)[permission]);
      case ScheduleType.Sales:
        return this.userContext.hasPermission((BuilderPermission.Schedules.Sales as any)[permission]);
      case ScheduleType.PreConstruction:
        return this.userContext.hasPermission((BuilderPermission.Schedules.PreConstruction as any)[permission]);
      case ScheduleType.Maintenance:
        return this.userContext.hasPermission((BuilderPermission.Schedules.Maintenance as any)[permission]);
      default:
        return false;
    }
  }

  ngOnInit(): void {
    const {
      taskDetails,
      scheduleTaskTodoEventType,
      scheduleTaskTypes,
      changeDateAfterCompleteTypes,
      noteGridLayout,
      todoGridLayout,
      systemRoles,
    } = getRouteInitData(this.route);
    const { type: scheduleType } = getRouteStaticData(this.route);
    this.scheduleId = getRouteParam(this.route, 'scheduleId');
    this.taskId = getRouteParam(this.route, 'taskId');
    this.systemRoles = systemRoles;
    this.scheduleType = scheduleType;
    this.taskDetails = taskDetails;
    this.scheduleTaskTodoEventType = scheduleTaskTodoEventType;
    this.scheduleTaskTypes = scheduleTaskTypes;
    this.changeDateAfterCompleteTypes = changeDateAfterCompleteTypes;
    this.ccResourceGridDefinition = this.buildCcResourceGridDefinition();
    this.userResourceGridDefinition = this.buildUserResourceGridDefinition();
    this.notesGridDefinition = this.buildNotesGridDefinition();
    this.notesGridDefinition.bind(noteGridLayout);
    this.todoGridDefinition = this.buildTodoGridDefinition();
    this.todoGridDefinition.bind(todoGridLayout);
    this.currentTaskListGridDefinition = this.buildCurrentTaskListGridDefinition();

    this.form.build(taskDetails, this.isFormEditMode());
  }

  onCostCenterResourceChange(row: ScheduleTaskCcResourceListItem | undefined): void {
    if (!row || !this.form.editMode) {
      return;
    }
    const { supplierName, currentTasks, supplierId, costCenterId } = row;
    this.form.updateTaskInfoSnapshot(setFormStateSnapshotValue(
      this.form.info,
      (value: ScheduleTaskInfo) => setEntityField(value, 'resource', supplierName),
    ));
    // Update Current Tasks List grid
    this.form.updateCurrentTasks(map(currentTasks, createTaskCurrentTaskListItem));
    this.form.updateSupplierContactId(costCenterId, supplierId);
    this.form.updateUserId(void 0);
  }

  onUserResourceChange(row: ScheduleTaskUserResourceListItem | undefined): void {
    if (!row || !this.form.editMode) {
      return;
    }
    const { name, currentTasks, userId } = row;
    this.form.updateTaskInfoSnapshot(setFormStateSnapshotValue(
      this.form.info,
      (value: ScheduleTaskInfo) => setEntityField(value, 'resource', name),
    ));
    // Update Current Tasks List grid
    this.form.updateCurrentTasks(map(currentTasks, createTaskCurrentTaskListItem));
    this.form.updateUserId(userId);
    this.form.updateSupplierContactId(void 0, void 0);
  }

  onSelectedTabChanged(event: any): void {
    const tabName = event.name;
    this.form.selectTab(tabName);
  }

  onSelectedGeneralSubtabChanged(event: any): void {
    const subTabName = event.name as ScheduleTaskDetailsGeneralSubtabName;
    this.form.selectGeneralSubtab(subTabName);
  }

  onTopFormDataChange(taskInfoSnapshot: FormStateSnapshot<ScheduleTaskInfo>): void {
    this.form.updateTaskInfoSnapshot(taskInfoSnapshot);
  }

  onDeleteNote(note: WithUniqueId<ScheduleTaskNoteListItem>): void {
    this.dialogService.confirmation(yesCancelConfirmation('msg_sch_task_note_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeNote(note));
  }

  onEditNote(note: WithUniqueId<ScheduleTaskNoteListItem>): void {
    this.dialogService.openWithInput(
      scheduleTemplateNoteListItemDialog,
      <EntityInputDialogConfig<ScheduleTaskNoteListItem>>{
        entity: note,
        titleId: this.form.viewMode ? 'lbl_note' : 'lbl_edit_note',
        placeholderId: 'lbl_note',
        field: 'memo',
        required: true,
        showTextArea: true,
        id: note.scheduleTaskNoteId,
        minHeight: 300,
        readOnly: this.form.viewMode,
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedNote: ScheduleTaskNoteListItem) => this.form.updateNote(note, updatedNote));
  }

  onAddNote(): void {
    this.dialogService.openWithInput(
      scheduleTemplateNoteListItemDialog,
      <EntityInputDialogConfig<ScheduleTaskNoteListItem>>{
        titleId: 'lbl_add_note',
        placeholderId: 'lbl_note',
        saveButtonLabelId: 'lbl_add',
        field: 'memo',
        required: true,
        showTextArea: true,
        minHeight: 300,
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((newNote: ScheduleTaskNoteListItem) => this.form.addNote(newNote));
  }

  onAddTodo(): void {
    this.dialogService.openWithInput(scheduleTaskTodoDialog, <ScheduleTaskTodoDialogConfig>{
      scheduleType: this.scheduleType,
      event: ScheduleTaskTodoEventType.BeforeStart,
      days: DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE,
      mode: FormMode.CreateMode,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((newTodo: ScheduleTaskTodoListItem) => this.form.addTodo(newTodo));
  }

  onEditTodo(todo: WithUniqueId<ScheduleTaskTodoListItem>): void {
    this.dialogService.openWithInput(scheduleTaskTodoDialog, <ScheduleTaskTodoDialogConfig>{
      scheduleType: this.scheduleType,
      scheduleTaskTodoId: todo.scheduleTaskTodoId,
      days: todo.days,
      event: todo.event,
      systemRole: todo.systemRole,
      memo: todo.memo,
      user: todo.user,
      mode: this.form.editable ? FormMode.EditMode : FormMode.ViewMode,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedTodo: WithUniqueId<ScheduleTaskTodoListItem>) =>
        this.form.updateTodo(todo, updatedTodo));
  }

  onDeleteTodo(todo: WithUniqueId<ScheduleTaskTodoListItem>): void {
    this.dialogService.confirmation(yesCancelConfirmation('msg_sch_task_todo_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeTodo(todo));
  }

  schSaveBaseDatesDialog(task: ScheduleTaskDetails): Observable<any> {
    if (task.info.value.completionPct && task.info.value.completionPct === 100 && !task.isBaseDatesSavedBefore) {
      return this.dialogService.confirmation(yesNoConfirmation('msg_sch_save_base_dates'))
                 .pipe(map$((answer) => {
        this.saveBaseDatesAnswer = answer;
        this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
      }));
    }
    this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
    return of$(true);
  }

  private buildCcResourceGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'code',
      width: 150,
      headerName: this.translationService.get('lbl_number'),
    }, {
      field: 'name',
      width: 300,
      headerName: this.translationService.get('lbl_description'),
    }, {
      field: 'supplierName',
      width: 300,
      headerName: this.translationService.get('lbl_name'),
    }, {
      field: 'supplierPhone',
      width: 200,
      headerName: this.translationService.get('lbl_phone'),
    }, {
      field: 'supplierContact',
      width: 400,
      headerName: this.translationService.get('lbl_contact'),
    }, {
      field: 'isPreferred',
      width: 150,
      headerName: this.translationService.get('lbl_preferred'),
      cellRenderer: truthyCellRenderer(rendererIconByName('done')),
    }], {
      rowClassRules: {
        [ROW_BOLD_CLASS]: ({ data }: any) => !!data.isPreferred || !!data.isCcSupplier,
      },
      getRowId: (params) =>
        String(`${params.data.supplierId}${params.data.costCenterId}`),
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
    });
  }

  private buildUserResourceGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'name',
      width: 300,
    }], {
      getRowId: (params) => String(params.data.userId),
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
    });
  }

  private buildNotesGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased({
      addedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
    }, {
      getRowId: (params) => String(getScheduleTaskNoteId(params.data)),
    });
  }

  private buildTodoGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased({
      systemRole: {
        valueFormatter: dictionaryValueFormatter(this.systemRoles),
      },
      user: {
        valueGetter: ({ data }: { data: ScheduleTaskTodoListItem }) => data.user.userName,
      },
      addedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      event: {
        valueGetter: this.todoGridEventValueGetter.bind(this),
      },
    }, {
      getRowId: (params) => String(getScheduleTaskTodoId(params.data)),
    });
  }

  private buildCurrentTaskListGridDefinition(): GridDefinition {
    const colDefs: ColDef[] = [{
      field: 'type',
      width: 150,
      headerName: this.translationService.get('lbl_type'),
      valueFormatter: dictionaryValueFormatter(this.scheduleTaskTypes),
    }, {
      field: 'scheduleFor',
      width: 150,
      headerName: this.translationService.get('lbl_schedule_for'),
    }, {
      field: 'name',
      width: 150,
      headerName: this.translationService.get('lbl_task'),
    }, {
      field: 'startDate',
      width: 150,
      headerName: this.translationService.get('lbl_start_date'),
      ...buildDateTimeColumn(DateTimeTypes.DateTime),
    }, {
      field: 'endDate',
      width: 150,
      headerName: this.translationService.get('lbl_end_date'),
      ...buildDateTimeColumn(DateTimeTypes.DateTime),
    }, {
      field: 'duration',
      width: 150,
      headerName: this.translationService.get('lbl_duration'),
      valueFormatter: cellValueFormatter<number>((duration) =>
        this.localizationBusinessService.formatHoursDuration(duration)),
    }, {
      field: 'completionPct',
      width: 150,
      headerName: this.translationService.get('lbl_completion_pct'),
    }];

    return this.gridDefinitionFactory.buildManual(colDefs, {
      noAutomation: true,
    });
  }

  private todoGridEventValueGetter(params: ValueGetterParams): string {
    const { days } = params.data;
    const dayLabel = this.translationService.get(days === 1 ? 'lbl_day' : 'lbl_days');
    
    return lowerCase(`${days} ${dayLabel} ${this.scheduleTaskTodoEventType.get(params.data.event)}`);
  }

  private isScheduleEditable(): boolean {
    const details = this.taskDetails;
    switch (this.scheduleType) {
      case ScheduleType.Construction:
      case ScheduleType.PreConstruction:
        return !(details.isFinalized || details.isNotProceeding);
      case ScheduleType.Maintenance:
        return !details.isNotProceeding;
      case ScheduleType.Sales:
        return details.isSalesFolderActive;
      default:
        return false;
    }
  }

  private isFormEditMode(): boolean {
    return this.hasPermission('EDIT') && this.isScheduleEditable();
  }
}
